import { useEffect, useRef, type ReactNode } from 'react';
import { useI18n } from '../i18n';

/**
 * The one way this screen shows you something extra, and it comes up from the bottom.
 *
 * The specification says a "hover" reveals what the learner asked for. There is no hover on
 * a phone held in one hand, so the question is what a tap should do instead, and the wrong
 * answers are worth naming because both are common. A tooltip anchored to the tapped
 * element is a hover in a costume: it appears under the thumb that summoned it, it has
 * nowhere to go on a narrow screen, and there is no obvious way to dismiss it. Navigating
 * to a detail screen is the other extreme — it throws away the conversation, and the whole
 * point of tapping a briefing is to look at something *while* the briefing is still there.
 *
 * A sheet from the bottom is neither. It comes up under the thumb rather than above it, it
 * takes the width it needs, it leaves the top of the thread visible so the learner can see
 * they have not left, and it has two obvious ways out — the scrim and the button — plus
 * Escape for a keyboard.
 *
 * **It is deliberately not a `<dialog>`.** `showModal` gives focus trapping and the
 * top layer for free, and it also gives Safari's own animation, its own backdrop, and an
 * element that cannot be positioned inside the chat's flex column — so on iOS it renders
 * against the layout viewport, which is the exact coordinate space `viewport.ts` exists to
 * stop this screen from using. The focus handling it would have given us is the twenty
 * lines below.
 */
export default function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const panel = useRef<HTMLDivElement | null>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    /*
     * Where focus came from, so it can go back.
     *
     * Without this, closing the sheet drops focus onto the body and a keyboard user is
     * returned to the top of the document — which on this screen means tabbing back through
     * the entire thread to reach the message they tapped. Captured before focus is moved,
     * because after that the answer is the sheet itself.
     */
    opener.current = document.activeElement;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const returning = opener.current;
      if (returning instanceof HTMLElement && returning.isConnected) returning.focus();
    };
  }, [onClose]);

  return (
    <div className="chat-sheet-scrim" onClick={onClose}>
      {/*
        The click is stopped here rather than bound to the scrim's own target, because a
        drag that starts on the panel and ends on the scrim — selecting the text of a
        briefing, which is exactly what somebody does in this sheet — dispatches its click
        on the common ancestor and would dismiss the thing they were reading.
      */}
      <div
        className="chat-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="chat-sheet-grip" aria-hidden="true" />
        <div className="chat-sheet-head">
          <h2 className="chat-sheet-title">{title}</h2>
          <button type="button" className="chat-sheet-close" onClick={onClose}>
            {t('chat.close')}
          </button>
        </div>
        <div className="chat-sheet-body">{children}</div>
      </div>
    </div>
  );
}
